import React, {useMemo} from 'react';

import {useGate, useStore} from 'effector-react';

import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {exportRecallAuditLog} from 'api/recallAudit';

import {AuditExportControl} from 'components/common/AuditLog/ExportControl';
import {AuditNavigation} from 'components/common/AuditLog/Navigation';
import {Page} from 'components/common/Page';

import {DateRange} from 'components/ui-kit/DateRange';

import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';

import {Select, SelectOption} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';

import {ROUTE} from 'core/routes';
import {isAdmin$, isNkRole$, profile$} from 'models/auth';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    pageSizeChanged,
    sortingsChanged,
    tableState$,
} from 'models/recallAudit';
import {mapRecallAuditFilters} from 'models/recallAudit/mappers';
import {GridFieldConfig, UserRoles} from 'types';
import {RecallAuditAccessors, RecallAuditEntry, RecallAuditEventType} from 'types/recall-audit';
import {handleNil} from 'utils/handleNil';

import {BreakAllValue, EventId, PageContent, Toolbar} from './styled';
import {formatLocalDateTime, translateUserRole} from './utils';

interface MappedAuditRow extends Record<string, React.ReactNode> {
    entry: RecallAuditEntry;
}

const getColumns = (
    t: TFunction,
    eventTypeOptions: SelectOption<RecallAuditEventType>[],
    showEventTypeFilter: boolean,
): GridFieldConfig[] => [
    {
        id: RecallAuditAccessors.eventId,
        header: t('pages.recallAudit.columns.eventId'),
        width: 230,
    },
    {
        id: RecallAuditAccessors.batchId,
        header: t('pages.recallAudit.columns.batchId'),
        width: 120,
    },
    {
        id: RecallAuditAccessors.eventType,
        header: t('pages.recallAudit.columns.eventType'),
        width: 210,
        filter: showEventTypeFilter ? <Select isClearable options={eventTypeOptions} /> : undefined,
    },
    {
        id: RecallAuditAccessors.organizationName,
        header: t('pages.recallAudit.columns.organization'),
        width: 180,
        filter: <Input />,
    },
    {
        id: RecallAuditAccessors.organizationTin,
        header: t('pages.recallAudit.columns.tin'),
        width: 130,
        filter: <Input />,
    },
    {
        id: RecallAuditAccessors.eventTimestamp,
        header: t('pages.recallAudit.columns.dateTime'),
        width: 170,
        filter: <DateRange />,
    },
    {
        id: RecallAuditAccessors.userFullName,
        header: t('pages.recallAudit.columns.userName'),
        width: 180,
        filter: <Input />,
    },
    {
        id: RecallAuditAccessors.userRole,
        header: t('pages.recallAudit.columns.userRole'),
        width: 160,
    },
];

export const RecallAuditLogPage = () => {
    const {t} = useTranslation();
    useGate(Gate);

    const {data, pageNumber, pageSize, filters, sortings, isLoading} = useStore(tableState$);
    const profile = useStore(profile$);
    const isNkRole = useStore(isNkRole$);
    const isAdmin = useStore(isAdmin$);
    const canExport = profile?.roles.some((role) =>
        [UserRoles.ROLE_MPL_OPERATOR_ADMIN, UserRoles.ROLE_MPL_TS_DIRECTOR].includes(role),
    );
    const showEventTypeFilter = (isNkRole || isAdmin) ?? false;

    const eventTypeOptions = useMemo(
        () =>
            Object.values(RecallAuditEventType).map((value, index) => ({
                id: String(index),
                value,
                label: t(`pages.recallAudit.eventTypes.${value}`),
            })),
        [t],
    );
    const columns = useMemo(
        () =>
            mapTableColumns(
                getColumns(t, eventTypeOptions, showEventTypeFilter),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [eventTypeOptions, filters, sortings, t],
    );
    const mappedData = useMemo<MappedAuditRow[]>(
        () =>
            (data?.data ?? []).map((entry) => ({
                entry,
                [RecallAuditAccessors.eventId]: (
                    <EventId>
                        <Link
                            to={generatePath(ROUTE.RECALL_AUDIT_DETAIL, {
                                id: entry.eventId,
                            })}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {entry.eventId}
                        </Link>
                    </EventId>
                ),
                [RecallAuditAccessors.batchId]: handleNil(entry.batchId),
                [RecallAuditAccessors.eventType]: t(
                    `pages.recallAudit.eventTypes.${entry.eventType}`,
                ),
                [RecallAuditAccessors.organizationName]: handleNil(
                    entry.organizationName ?? entry.organizationId,
                ),
                [RecallAuditAccessors.organizationTin]: handleNil(entry.organizationTin),
                [RecallAuditAccessors.eventTimestamp]: formatLocalDateTime(entry.eventTimestamp),
                [RecallAuditAccessors.userFullName]: handleNil(entry.userFullName ?? entry.userId),
                [RecallAuditAccessors.userRole]: (
                    <BreakAllValue>{translateUserRole(t, entry.userRole)}</BreakAllValue>
                ),
            })),
        [data, t],
    );

    const toolbar = (
        <Toolbar>
            {canExport && (
                <AuditExportControl
                    fileName="recall-audit"
                    initialPeriod={filters.eventTimestamp as [Date | null, Date | null]}
                    onExport={(format, period) => {
                        const mapped = mapRecallAuditFilters({...filters, eventTimestamp: period});
                        return exportRecallAuditLog({
                            ...mapped,
                            from: mapped.from!,
                            to: mapped.to!,
                            page: pageNumber,
                            size: pageSize,
                            format,
                        });
                    }}
                />
            )}
        </Toolbar>
    );

    return (
        <Page title={t('menu.activityLog')}>
            <PageContent>
                <AuditNavigation>{toolbar}</AuditNavigation>
                <Table
                    columns={columns}
                    data={mappedData}
                    pageIndex={pageNumber}
                    pageCount={data?.totalPages}
                    totalRowsCount={data?.totalElements}
                    onPageChange={pageNumberChanged}
                    onPageSizeChange={pageSizeChanged}
                    isLoading={isLoading}
                    hideSetting
                />
            </PageContent>
        </Page>
    );
};

export {RecallAuditDetailPage} from './Detail';
